import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button, Popconfirm, Space, Table, Tag, Tooltip, message } from "antd";
import S3Image from "../../../component/S3Image";
import type { ColumnsType } from "antd/es/table";
import { FiPlusCircle } from "react-icons/fi";
import { BiEdit } from "react-icons/bi";
import { RiDeleteBin6Line } from "react-icons/ri";
import { IoCopyOutline } from "react-icons/io5";
import { IoMdOpen } from "react-icons/io";
import { IoArrowBackCircleOutline } from "react-icons/io5";
import axios from "../../../config/axiosInstance";
import moment from "moment";

import { API } from "../../../config/API";
import Loadingbox from "../../../component/loadingbox";

import "../styles.css";

function NjayarArticlesView() {
  const { masterId } = useParams<{ masterId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [masterData, setMasterData] = useState<any>(null);
  const [settings, setSettings] = useState<any>(null);
  const [linkLoading, setLinkLoading] = useState(false);

  useEffect(() => {
    loadData();
    loadSettings();
    if (location?.state?.items) {
      setMasterData(location.state.items);
    } else if (masterId) {
      loadMasterData();
    }
  }, [masterId, location.key]);

  const loadData = () => {
    if (!masterId) return;
    setLoading(true);
    const url =
      API.BASE_URL +
      API.NJAYAR_PRABHAADHAM +
      `?filters[masterid][$eq]=${masterId}&sort=createdAt:asc`;
    axios
      .get(url)
      .then((response) => {
        setData(response.data.data || []);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };

  const loadMasterData = () => {
    const url = API.BASE_URL + API.NJAYAR_MASTER + `/${masterId}`;
    axios
      .get(url)
      .then((response) => {
        setMasterData(response.data.data || null);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const loadSettings = () => {
    const url = API.BASE_URL + API.SETTINGS_LIST;
    axios.get(url).then((response) => {
      setSettings(response?.data?.data?.[0]?.attributes);
    });
  };

  const onAddArticle = () => {
    navigate(
      `/Auth/unified-content/njayar/master/${masterId}/create?type=njayar`
    );
  };

  const onEditArticle = (record: any) => {
    navigate(
      `/Auth/unified-content/njayar/master/${masterId}/edit/njayar/${record.id}`
    );
  };

  const onDeleteArticle = (id: number) => {
    const url = API.BASE_URL + API.NJAYAR_PRABHAADHAM + `/${id}`;
    axios
      .delete(url)
      .then(() => {
        message.success("Article deleted");
        loadData();
      })
      .catch((err) => {
        console.log(err);
        message.error("Failed to delete");
      });
  };

  const generateLink = (record: any, type: "copy" | "preview") => {
    const attrs = record?.attributes || record;
    const link = attrs?.permalink
      ? attrs.permalink.replace(/\s+/g, "-")
      : attrs?.title?.replace(/\s+/g, "-");
    const url = `${API.APP_URL}njayarprabhaatham?id=${masterId}&article=${record?.id}&link=${link}`;
    const body = `${attrs?.title}\nRead more at: ${url}${
      settings?.whatsappJoinLink
        ? `\nStay updated with the latest news! Join our WhatsApp group: ${settings.whatsappJoinLink}`
        : ""
    }`;
    if (type === "copy") {
      navigator.clipboard.writeText(body).then(() => {
        message.success("Link copied");
      });
    } else if (type === "preview") {
      window.open(url, "_blank");
    }
  };

  const updateNjayarMaster = () => {
    if (!masterId || !data?.length) return;
    setLinkLoading(true);
    const masterAttrs = masterData?.attributes || masterData;
    const requestObj = {
      data: {
        head: masterAttrs?.head,
        items: data.map((item: any) => item.id),
      },
    };
    const url = API.BASE_URL + API.NJAYAR_MASTER + `/${masterId}`;
    axios
      .put(url, requestObj)
      .then(() => {
        setLinkLoading(false);
        message.success("Articles added to Njayar Prabhaatham");
      })
      .catch((error) => {
        console.log(error);
        setLinkLoading(false);
        message.error("Failed to update");
      });
  };

  const masterHead =
    masterData?.attributes?.head || masterData?.head || "Njayar Articles";

  interface DataType {
    key: string;
    id: number;
  }

  const columns: ColumnsType<DataType> = [
    {
      title: "#",
      key: "id",
      width: 50,
      render: (_text, _record, index) => (
        <span className="uc-table-cell-muted">{index + 1}</span>
      ),
    },
    {
      title: "Image",
      key: "image1",
      width: 80,
      render: (_text, record: any) => {
        const img = record?.attributes?.image1;
        return img ? (
          <S3Image
            src={img}
            className="uc-table-thumb"
            alt=""
          />
        ) : (
          <div className="uc-table-noimg">No image</div>
        );
      },
    },
    {
      title: "Title",
      key: "title",
      ellipsis: true,
      width: 300,
      render: (_text, record: any) => (
        <div
          className="uc-table-headline"
          onClick={() => onEditArticle(record)}
        >
          {record?.attributes?.title}
        </div>
      ),
    },
    {
      title: "Author",
      key: "Author",
      width: 120,
      render: (_text, record: any) => (
        <span className="uc-table-cell-sm">
          {record?.attributes?.Author}
        </span>
      ),
    },
    {
      title: "Date-Time",
      key: "createdAt",
      width: 150,
      render: (_text, record: any) => (
        <span className="uc-table-cell-sm">
          {moment(record?.attributes?.createdAt).format("DD MMM YYYY, h:mm A")}
        </span>
      ),
    },
    {
      title: "Status",
      key: "publishedAt",
      width: 110,
      render: (_text, record: any) => (
        <Tag
          className={
            record?.attributes?.publishedAt
              ? "uc-status-published"
              : "uc-status-draft"
          }
        >
          {record?.attributes?.publishedAt ? "Published" : "Draft"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 140,
      render: (_, record: any) => (
        <div className="uc-table-actions">
          {record?.attributes?.publishedAt && (
            <>
              <Tooltip title="Preview">
                <IoMdOpen
                  color="#666"
                  size={17}
                  cursor="pointer"
                  onClick={() => generateLink(record, "preview")}
                />
              </Tooltip>
              <Tooltip title="Copy link">
                <IoCopyOutline
                  size={17}
                  color="#52c41a"
                  cursor="pointer"
                  onClick={() => generateLink(record, "copy")}
                />
              </Tooltip>
            </>
          )}
          <Tooltip title="Edit">
            <BiEdit
              size={17}
              color="#faad14"
              cursor="pointer"
              onClick={() => onEditArticle(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete article?"
            description="Are you sure you want to delete this article?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => onDeleteArticle(record.id)}
          >
            <RiDeleteBin6Line size={17} color="#ff4d4f" cursor="pointer" />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="uc-form-page">
      {/* Top Bar */}
      <div className="uc-topbar">
        <div className="uc-topbar-row">
          <div
            className="uc-topbar-back"
            onClick={() => navigate("/Auth/unified-content?tab=njayar")}
          >
            <IoArrowBackCircleOutline />
          </div>
          <div>
            <div className="uc-topbar-title">{masterHead}</div>
            <div className="uc-topbar-subtitle">
              {data?.length || 0} article{data?.length !== 1 ? "s" : ""}
            </div>
          </div>
          <div className="uc-topbar-spacer" />
          <div className="uc-topbar-actions">
            <Button type="primary" onClick={onAddArticle}>
              Add Articles &nbsp; <FiPlusCircle />
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <Loadingbox />
      ) : (
        <div className="uc-content">
          <Table
            className="uc-table"
            columns={columns}
            dataSource={data}
            rowKey={(record) => record.id}
            pagination={false}
            size="small"
            scroll={{ x: 1000 }}
          />
          {data?.length > 0 && (
            <div style={{ padding: "16px 24px", display: "flex", justifyContent: "flex-end" }}>
              <Button
                type="primary"
                loading={linkLoading}
                onClick={updateNjayarMaster}
              >
                Add Articles to Njayar Prabhaatham
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NjayarArticlesView;
